import { ERREURS } from './catalogueErreurs';
import { normalizeAnswer, parseComplexAnswer } from './questionUtils';

/**
 * « L'analyse de TanoEd » : ce que le tableau de bord montre de la route
 * bilan-attendus du back (classe ou groupe), V1 du 4 octobre 2026. Le back
 * décide des états, des alertes et de l'ordre ; le front les présente sans
 * refaire les règles (validé par le prof, épreuve de Musk) :
 *
 * - L'unité est l'ATTENDU. L'état de chaque élève (`etats` : à reprendre,
 *   progresse, à confirmer, acquis, à vérifier, sans donnée) se lit sur ses
 *   séances ; `alerte` vaut `priorite` (au moins 3 à reprendre) ou `signal`
 *   (au moins 3 élèves avec une erreur retenue des 14 jours), sinon rien, ce
 *   qui ne veut pas dire que tout va bien.
 * - Deux blocs dans l'ordre du back, 3 cartes au plus chacun : les priorités,
 *   puis les signaux. « Par savoir » : tous les attendus, les besoins d'un ou
 *   deux élèves compris.
 * - Une seule liste d'élèves par attendu (décision du prof, 08/10, épreuve de
 *   Musk) : les élèves à reprendre, et eux seuls ; sans élève à reprendre, ceux
 *   qui se sont trompés ces 14 jours, quel que soit leur état. Le nombre de la
 *   ligne, la liste de la fenêtre et les élèves envoyés vers Créer en viennent
 *   tous : un élève acquis qui s'est trompé une fois n'y entre pas.
 * - Le diagnostic (`diagnostic_14j`) lit les erreurs de tous les élèves, quel
 *   que soit leur état (comprendre) ; l'erreur montrée est celle des élèves de la
 *   liste (agir). Le prof reprend une règle, pas une erreur : les erreurs nommées
 *   dont la règle est le même texte se regroupent ; la carte prend la règle que
 *   font le plus d'élèves de la liste (à égalité, celle qui a le plus d'élèves en
 *   tout, puis l'ordre du back), ses erreurs rangées de même. Une erreur qu'aucun
 *   élève de la liste n'a faite n'est pas la leur : elle n'est pas montrée. Une
 *   priorité peut ne venir que de notes : rien à montrer, rien d'inventé.
 *
 * Le back n'envoie que des numéros et des codes : les prénoms viennent de
 * `eleves`, le libellé et le sous-titre du catalogue (catalogueErreurs.js).
 */

export const NB_CARTES = 3;
export const NB_EXEMPLES = 3;
/** Les erreurs d'une même règle montrées sur la carte ; les suivantes se comptent (« et 1 autre erreur »). */
export const NB_ERREURS = 2;

/** Les six états du back, dans l'ordre où l'écran les nomme. « Sans donnée » (aucun jour qui tranche,
 * même pour un élève qui a répondu une ou deux fois) se dit « pas assez d'observations ». */
export const ETATS = [
    ['a_reprendre', 'à reprendre'], ['progresse', 'progresse'], ['a_confirmer', 'à confirmer'],
    ['acquis', 'acquis'], ['a_verifier', 'à vérifier'], ['sans_donnee', 'pas assez d\'observations'],
];

const parNom = (a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base', numeric: true });
const memeReponse = (a, b) => normalizeAnswer(String(a ?? '').trim()) === normalizeAnswer(String(b ?? '').trim());

/** Le nom affiché de chaque élève : le prénom, avec l'initiale du nom quand deux élèves le partagent. */
export const nomsDesEleves = (eleves = []) => {
    const fiches = eleves.filter(Boolean);
    const combien = new Map();
    fiches.forEach((e) => {
        const prenom = String(e.prenom ?? '').trim();
        combien.set(prenom, (combien.get(prenom) || 0) + 1);
    });
    return new Map(fiches.map((e) => {
        const prenom = String(e.prenom ?? '').trim();
        const nom = String(e.nom ?? '').trim();
        if (!prenom) return [e.id, nom];
        return [e.id, combien.get(prenom) > 1 && nom ? `${prenom} ${nom.charAt(0)}.` : prenom];
    }));
};

// ─── Les réponses des exemples, telles que le back les rend ─────────────────

const TROU_RE = /\{\{([^{}]*)\}\}/g;
const trousDe = (ligne) => [...String(ligne).matchAll(TROU_RE)].map((m) => m[1]);
// Un trou laissé vide se lit « ? » (en texte comme dans une formule).
const remplir = (ligne) => String(ligne).replace(TROU_RE, (m, mot) => (mot.trim() ? mot : '?'));
const json = (texte) => { try { return JSON.parse(texte); } catch { return undefined; } };

/** Un tableau vrai/faux n'est fait QUE de blocs [phrase|valeur] (même lecture que le back). */
const lignesVraiFaux = (texte) => {
    const s = String(texte ?? '').trim();
    if (!s.startsWith('[')) return null;
    const lignes = parseComplexAnswer(s);
    if (!lignes) return null;
    return lignes.map((l) => `[${l.phrase}|${l.reponse}]`).join('') === s.replace(/\]\s+\[/g, '][') ? lignes : null;
};

/**
 * Ce qu'un exemple donne à lire : seulement ce que l'élève a manqué, en regard
 * de ce qui était attendu. Pour une erreur nommée, le back envoie le MORCEAU de
 * réponse qui porte le code (trouverErreurs : une ligne, un choix, une paire,
 * un point), pour un exercice sans nom la réponse entière. Formes : `simple`
 * (QCM, saisie), `multiple` (QCM à plusieurs réponses), `trous` (les lignes
 * fausses, trous remplis), `vf` (les lignes mal jugées), `paires` (relier,
 * placer : les sources ou points mal placés).
 */
export const lireExemple = (exemple = {}) => {
    const bonne = String(exemple.bonne_reponse ?? '');
    const donnee = String(exemple.reponse_donnee ?? '');
    const enonce = exemple.enonce || null;

    const tableau = lignesVraiFaux(bonne);
    if (tableau) {
        const jugees = new Map((lignesVraiFaux(donnee) || []).map((l) => [l.phrase, l.reponse]));
        return {
            forme: 'vf',
            enonce,
            lignes: tableau.filter((l) => !memeReponse(jugees.get(l.phrase), l.reponse))
                .map((l) => ({ phrase: l.phrase, donnee: jugees.get(l.phrase) ?? '', bonne: l.reponse })),
        };
    }
    if (bonne.includes('{{')) {
        // La phrase à trous EST l'énoncé : on montre ses lignes fausses, pas l'énoncé au-dessus.
        const lb = bonne.split('\n');
        const ld = donnee.split('\n');
        if (lb.length !== ld.length) return { forme: 'simple', enonce: null, donnee: remplir(donnee), bonne: remplir(bonne) };
        return {
            forme: 'trous',
            enonce: null,
            lignes: lb.map((b, i) => ({ b, d: ld[i] }))
                .filter(({ b, d }) => { const attendus = trousDe(b); const mots = trousDe(d); return attendus.some((m, k) => !memeReponse(mots[k], m)); })
                .map(({ b, d }) => ({ donnee: remplir(d), bonne: remplir(b) })),
        };
    }
    const structure = /^\s*[[{]/.test(bonne) ? json(bonne) : undefined;
    if (Array.isArray(structure) && structure.length && structure.every(Array.isArray)) {
        // Relier : des paires [source, cible] ; seulement les sources mal reliées.
        const reliees = new Map((json(donnee) || []).filter(Array.isArray).map((p) => [String(p[0]), String(p[1] ?? '')]));
        return {
            forme: 'paires',
            enonce,
            lignes: structure.filter((p) => !memeReponse(reliees.get(String(p[0])), p[1]))
                .map((p) => ({ cle: String(p[0]), donnee: reliees.get(String(p[0])) ?? '', bonne: String(p[1] ?? '') })),
        };
    }
    if (Array.isArray(structure)) {
        const cochees = json(donnee);
        return { forme: 'multiple', enonce, donnee: Array.isArray(cochees) ? cochees.map(String) : [donnee], bonne: structure.map(String) };
    }
    if (structure && typeof structure === 'object') {
        const donnees = json(donnee) || {};
        return {
            forme: 'paires',
            enonce,
            lignes: Object.keys(structure).filter((k) => !memeReponse(donnees[k], structure[k]))
                .map((k) => ({ cle: k, donnee: donnees[k] === undefined ? '' : String(donnees[k]), bonne: String(structure[k]) })),
        };
    }
    return { forme: 'simple', enonce, donnee, bonne };
};

// Les réponses simples d'abord : l'erreur s'y lit sans démêler des lignes.
const ORDRE = { simple: 0, multiple: 1, trous: 2, paires: 3, vf: 4 };
const aMontrer = (lu) => (lu.lignes ? lu.lignes.length > 0 : true);

const lisibles = (exemples, noms) => (exemples || [])
    // Sans énoncé, un exemple ne se lit pas : le nom de la carte (« 81 est le carré de… ») passerait pour la question.
    .filter((x) => x && typeof x.enonce === 'string' && x.enonce.trim())
    .map((x) => ({ eleve: noms.get(x.eleve_id) || '', jour: x.jour || '', lu: lireExemple(x) }))
    .filter((x) => aMontrer(x.lu))
    .sort((a, b) => ORDRE[a.lu.forme] - ORDRE[b.lu.forme]);

/** Les réponses de plusieurs erreurs : à tour de rôle, une par erreur (la plus fréquente d'abord), trois au plus. */
const exemplesLisibles = (listes, noms) => {
    const files = listes.map((exemples) => lisibles(exemples, noms));
    const tours = Math.max(0, ...files.map((f) => f.length));
    return Array.from({ length: tours }, (_, rang) => files.map((f) => f[rang]).filter(Boolean)).flat().slice(0, NB_EXEMPLES);
};

// ─── Les attendus ───────────────────────────────────────────────────────────

const ids = (liste, noms) => (Array.isArray(liste) ? liste.filter((id) => noms.has(id)) : []);
/** Des élèves connus, chacun une fois, rangés par nom. */
const elevesConnus = (liste, noms) => [...new Set(ids(liste, noms))].map((id) => ({ id, nom: noms.get(id) })).sort((x, y) => parNom(x.nom, y.nom));

/** L'état de chaque élève sur un attendu, lu dans `etats` (jamais recalculé ici). */
const etatsParEleve = (etats) => {
    const m = new Map();
    ETATS.forEach(([cle]) => ((etats || {})[cle] || []).forEach((id) => { if (!m.has(id)) m.set(id, cle); }));
    return m;
};

/** Les erreurs nommées connues du catalogue, réunies par règle (même texte) ; une erreur sans règle reste seule. */
const groupesParRegle = (erreurs, noms) => {
    const groupes = new Map();
    (erreurs || []).forEach((e) => {
        const nommee = e && ERREURS[e.code];
        const eleves = nommee ? [...new Set(ids(e.eleves, noms))] : [];
        if (!eleves.length) return;
        const cle = nommee.regle ? `regle:${nommee.regle}` : `code:${e.code}`;
        if (!groupes.has(cle)) groupes.set(cle, { regle: nommee.regle || '', erreurs: [], eleves: new Set() });
        const g = groupes.get(cle);
        g.erreurs.push({ e, eleves });
        eleves.forEach((id) => g.eleves.add(id));
    });
    return [...groupes.values()];
};
const versCarte = ({ e }) => ({ code: e.code, titre: ERREURS[e.code].libelle, sousTitre: ERREURS[e.code].sousTitre || '' });

const ficheAttendu = (a, noms) => {
    const etatDe = etatsParEleve(a.etats);
    const persistants = elevesConnus(a.persistants, noms);
    const sontPersistants = new Set(persistants.map((e) => e.id));
    // Chaque élève montré avec son état réel (un élève sans assez d'observations peut s'être trompé), et
    // s'il est persistant : ses deux derniers jours qui ont tranché sont des échecs.
    const avecEtat = (e) => ({ ...e, etat: etatDe.get(e.id) || 'sans_donnee', persistant: sontPersistants.has(e.id) });
    const aReprendre = elevesConnus((a.etats || {}).a_reprendre, noms);
    const signal = elevesConnus(a.eleves_signal_14j, noms);
    // La liste de la ligne, une seule : les élèves à reprendre ; sans élève à reprendre, ceux qui se sont trompés.
    const listeDuSignal = !aReprendre.length;
    const eleves = (listeDuSignal ? signal : aReprendre).map(avecEtat);
    const sontDansLaListe = new Set(eleves.map((e) => e.id));
    const dansLaListe = (liste) => liste.filter((id) => sontDansLaListe.has(id)).length;
    // Le plus d'élèves de la liste d'abord, puis le plus d'élèves en tout ; à égalité, l'ordre du back (tri stable).
    const parListe = (x, y) => (dansLaListe(y) - dansLaListe(x)) || (y.length - x.length);
    const diag = a.diagnostic_14j || {};
    const principal = groupesParRegle(diag.erreurs, noms)
        .filter((g) => dansLaListe([...g.eleves]) > 0)
        .sort((g, h) => parListe([...g.eleves], [...h.eleves]))[0] || null;
    const erreurs = principal ? [...principal.erreurs].sort((x, y) => parListe(x.eleves, y.eleves)) : [];
    // L'erreur d'abord (les erreurs de la règle, rangées de même), puis la règle.
    const diagnostic = principal ? { regle: principal.regle, erreurs: erreurs.map(versCarte) } : null;
    // Les réponses : celles des erreurs de la règle, de n'importe quel élève ; sinon celles de l'exercice raté
    // sans erreur identifiée ; sinon aucune (une priorité qui ne vient que de notes n'a rien à montrer).
    const sansNom = (diag.exercices_sans_erreur_identifiee || []).find((s) => s && (s.exemples || []).length);
    const exemples = exemplesLisibles(principal ? erreurs.map(({ e }) => e.exemples) : [sansNom ? sansNom.exemples : []], noms);
    return {
        cle: `attendu:${a.attendu_id}`,
        attendu: a.attendu_id,
        titre: String(a.description || ''),
        niveau: a.niveau,
        horsNiveau: Boolean(a.hors_niveau),
        alerte: a.alerte === 'priorite' || a.alerte === 'signal' ? a.alerte : null,
        aReprendre,
        persistants,
        signal,
        listeDuSignal,
        eleves,
        diagnostic,
        exemples,
        cible: { code: diagnostic ? diagnostic.erreurs[0].code : null, eleves: eleves.map((e) => e.id) },
    };
};

/** Les priorités puis les signaux, dans l'ordre du back, 3 cartes au plus chacun, et tous les attendus. */
export const analyseTanoEd = (bilan, { nbCartes = NB_CARTES } = {}) => {
    if (!bilan) return { priorites: [], nbPriorites: 0, signaux: [], nbSignaux: 0, tous: [] };
    const noms = nomsDesEleves(bilan.eleves || []);
    const tous = (bilan.attendus || []).filter((a) => a && a.attendu_id !== undefined).map((a) => ficheAttendu(a, noms));
    const priorites = tous.filter((f) => f.alerte === 'priorite');
    const signaux = tous.filter((f) => f.alerte === 'signal');
    return {
        priorites: priorites.slice(0, nbCartes),
        nbPriorites: priorites.length,
        signaux: signaux.slice(0, nbCartes),
        nbSignaux: signaux.length,
        tous,
    };
};
